import React, { useEffect, useState } from "react";
import { Loader2, PiggyBank, Check, AlertTriangle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Switch } from "@/components/ui/switch";

/**
 * PurchaseControlsCard — Iteration 261 (PER-CHILD).
 *
 * "Purchase & Auto-Approval Controls" — sits directly above the Pending
 * Approvals / Moderation Queue on the Parent Dashboard. The parent picks
 * a child, then sets that child's Auto-Approve toggle + Instant Buy
 * threshold. Every change saves INSTANTLY via PUT /api/parent/auto-approve
 * and takes effect immediately for that child's Piggy Bank purchases.
 */
const THRESHOLDS_PENNIES = [500, 1000, 1500, 2000, 2500, 3000];
const gbp = (p) => `£${(p / 100).toFixed(2)}`;

export default function PurchaseControlsCard({ kids = [] }) {
  const [loading, setLoading] = useState(true);
  const [rules, setRules] = useState({});
  const [selectedKid, setSelectedKid] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedTick, setSavedTick] = useState(false);
  const [err, setErr] = useState("");
  // Fallback roster — the dashboard's bundled kids fetch can
  // transiently fail; the card must never strand without controls.
  const [fetchedKids, setFetchedKids] = useState([]);
  const kidsList = kids.length ? kids : fetchedKids;

  useEffect(() => {
    let live = true;
    api.get("/parent/auto-approve")
      .then(({ data }) => { if (live) setRules(data?.rules || {}); })
      .catch(() => { /* defaults stay OFF per child */ })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, []);

  useEffect(() => {
    if (kids.length) return;
    let live = true;
    api.get("/kids")
      .then(({ data }) => { if (live) setFetchedKids(Array.isArray(data) ? data : []); })
      .catch(() => {});
    return () => { live = false; };
  }, [kids]);

  // Default-select the first child once the roster arrives.
  useEffect(() => {
    if (!selectedKid && kidsList.length) setSelectedKid(kidsList[0].id);
  }, [kidsList, selectedKid]);

  const rule = rules[selectedKid] || {};
  const enabled = !!rule.enabled;
  const threshold = rule.threshold_pennies || 1000;

  const save = async (nextEnabled, nextThreshold) => {
    if (!selectedKid) return;
    setRules((prev) => ({
      ...prev,
      [selectedKid]: { enabled: nextEnabled, threshold_pennies: nextThreshold },
    }));
    setSaving(true);
    setErr("");
    try {
      await api.put("/parent/auto-approve", {
        kid_id: selectedKid,
        enabled: nextEnabled,
        threshold_pennies: nextThreshold,
      });
      setSavedTick(true);
      setTimeout(() => setSavedTick(false), 2000);
    } catch (e) {
      setErr(formatApiError(e) || "Couldn't save this setting — please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card-base p-6 md:p-8 mb-6" data-testid="purchase-controls-card">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="inline-flex items-center justify-center w-9 h-9 rounded-2xl bg-brandTeal text-white shadow-sm">
            <PiggyBank className="w-4 h-4" />
          </div>
          <div>
            <h2 className="font-display text-xl md:text-2xl font-medium text-slate-900">
              Purchase &amp; Auto-Approval Controls
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Set per child — changes apply instantly.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 min-h-[20px]" data-testid="auto-approve-status">
          {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
          {saving && <span className="text-xs text-slate-500 inline-flex items-center gap-1"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…</span>}
          {!saving && savedTick && (
            <span className="text-xs font-semibold text-emerald-700 inline-flex items-center gap-1" data-testid="auto-approve-saved">
              <Check className="w-3.5 h-3.5" /> Saved
            </span>
          )}
        </div>
      </div>

      {kidsList.length === 0 && !loading ? (
        <div className="rounded-2xl border border-slate-200 bg-slate-50/60 px-4 py-3 text-sm text-slate-500" data-testid="auto-approve-no-kids">
          Add a child profile first — each kid gets their own Instant Buy limit.
        </div>
      ) : (
        <>
          {/* Per-child selector */}
          <div className="flex flex-wrap gap-2 mb-4" data-testid="auto-approve-kid-bar" role="tablist" aria-label="Select child">
            {kidsList.map((k) => {
              const activeKid = k.id === selectedKid;
              const kidRule = rules[k.id] || {};
              return (
                <button
                  key={k.id}
                  type="button"
                  role="tab"
                  aria-selected={activeKid}
                  onClick={() => setSelectedKid(k.id)}
                  className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-bold border transition ${
                    activeKid
                      ? "bg-brandSlate text-white border-brandSlate shadow-sm"
                      : "bg-white text-slate-700 border-slate-200 hover:border-brandSlate"
                  }`}
                  data-testid={`auto-approve-kid-${k.id}`}
                >
                  <span aria-hidden="true">{k.avatar_emoji || "🧒"}</span> {k.name || "Child"}
                  {kidRule.enabled && (
                    <span className={`ml-1 w-2 h-2 rounded-full ${activeKid ? "bg-emerald-300" : "bg-emerald-500"}`} title="Instant Buy is ON" data-testid={`auto-approve-kid-on-${k.id}`} />
                  )}
                </button>
              );
            })}
          </div>

          {selectedKid && (
            <>
              <div className="flex items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-slate-50/60 px-4 py-3">
                <div className="min-w-0">
                  <div className="text-sm font-bold text-slate-900">Auto-Approve Piggy Bank Purchases</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    {enabled ? "ON — qualifying purchases complete instantly." : "OFF — every purchase waits for your manual approval."}
                  </div>
                </div>
                <Switch
                  checked={enabled}
                  disabled={loading || saving}
                  onCheckedChange={(v) => save(!!v, threshold)}
                  data-testid="auto-approve-toggle"
                  aria-label="Auto-Approve Piggy Bank Purchases"
                />
              </div>

              {enabled && (
                <div className="mt-4" data-testid="auto-approve-thresholds">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">
                    Auto-approval limit
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {THRESHOLDS_PENNIES.map((v) => (
                      <button
                        key={v}
                        type="button"
                        disabled={saving}
                        onClick={() => save(true, v)}
                        className={`px-3.5 py-2 rounded-full text-sm font-bold border transition disabled:opacity-60 ${
                          threshold === v
                            ? "bg-brandSlate text-white border-brandSlate shadow-sm"
                            : "bg-white text-slate-700 border-slate-200 hover:border-brandSlate"
                        }`}
                        data-testid={`auto-approve-threshold-${v}`}
                        aria-pressed={threshold === v}
                      >
                        {gbp(v)}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-slate-500 mt-3 leading-relaxed" data-testid="auto-approve-helper">
                    Purchases with a total cost (Item Price + 10% Fee) equal to or below this limit will complete automatically without requiring manual parent approval.
                  </p>
                </div>
              )}
            </>
          )}
        </>
      )}

      {err && (
        <div className="mt-3 rounded-xl bg-rose-50 border border-rose-200 px-3 py-2 text-xs text-rose-800 flex items-center gap-2" data-testid="auto-approve-error" role="alert">
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" /> {err}
        </div>
      )}
    </div>
  );
}
